import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Users, ExternalLink, Trash2, FileWarning } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { agencyApi, apiErr } from "./agencyApi";
import { StatusPill, OpenItemStatus, Lifecycle, when, groupDocs, stageChipLabel, DraftChip, handleAgencyMessageClick } from "./AgencyBits";
import { AgencyAvatar } from "./AgencyAvatar";
import AgencyCostSheet from "./AgencyCostSheet";
import { AgencyBtsGallery } from "./AgencyBtsGallery";
import { Lightbox } from "./Lightbox";
import { AgencyRecce } from "./AgencyRecce";

// Agency Portal Stabilisation, Wave A (2026-10) — "BTS" is now its OWN tab (was embedded on
// Overview only) so it reads as a first-class gallery, not a buried section.
const TABS = ["Overview", "BTS", "Open items", "Preferred crew", "Recce", "Cost sheet", "Documents", "Activity"];

export default function AgencyProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  // L4 (Cursor review, 2026-10) — a notification can deep-link here with ?tab=Recce&recce=<id>
  // (AgencyShell.jsx's notif click handler); read once on mount, same pattern as every other
  // "open this page already on the right tab" link in the app.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => (TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "Overview"));
  const initialRecceId = searchParams.get("recce");
  const [ws, setWs] = useState(null);
  const [items, setItems] = useState([]);
  const [crew, setCrew] = useState([]);
  const [docs, setDocs] = useState([]);
  const [err, setErr] = useState("");
  const [recceNeedsInputCount, setRecceNeedsInputCount] = useState(0);
  const { user } = useAuth();

  const loadCrew = useCallback(() => agencyApi.crew(id).then((r) => setCrew(r.data.requirements || [])).catch(() => {}), [id]);
  const loadItems = useCallback(() => agencyApi.openItems(id).then((r) => setItems(r.data.open_items || [])).catch(() => {}), [id]);

  useEffect(() => {
    agencyApi.workspace(id).then((r) => setWs(r.data)).catch((e) => setErr(apiErr(e)));
    loadItems(); loadCrew();
    agencyApi.documents(id).then((r) => setDocs(r.data.documents || [])).catch(() => {});
    // Wave R5 (2026-10) — cheap reuse of the SAME overview call AgencyRecce.jsx itself makes,
    // just to surface a count badge on the tab button before the tab is even opened.
    agencyApi.recce(id).then((r) => setRecceNeedsInputCount((r.data.needs_input || []).length)).catch(() => setRecceNeedsInputCount(0));
  }, [id, loadItems, loadCrew]);

  if (err) return <div className="ag-empty" data-testid="agency-detail-error">{err}</div>;
  if (!ws) return <div className="ag-small">Loading…</div>;
  const p = ws.project;

  return (
    <div data-testid="agency-project-detail">
      <button className="ag-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 18 }} onClick={() => nav("/agency/projects")}><ArrowLeft size={14} /> All projects</button>
      <div className="ag-detail-head">
        <div className="ag-detail-head-id">
          <h1 className="ag-h1 ag-entrance" style={{ fontSize: 34 }}>{p.name}</h1>
          <StatusPill status={stageChipLabel(p.stage, p.lost)} />
        </div>
        <div className="ag-detail-head-meta">
          <div style={{ textAlign: "right" }}>
            <div className="ag-label" style={{ marginBottom: 5 }}>Your NJM lead</div>
            <div className="ag-body" style={{ fontWeight: 600 }} data-testid="agency-detail-lead">{p.pm || "Being assigned"}</div>
          </div>
          {/* Cursor fix, items 6+7 (2026-10): THIS project's own resolved PM contact
              (workspace's `pm_contact`, pm_user_id-first) — the restyle used to call the
              account-wide /agency/pm-contact here instead, which always showed the FIRST
              assigned project's PM regardless of which project page you were on. Now also
              carries the Message (mailto) link the restyle had dropped from this page. */}
          {p.pm_contact && (
            <div style={{ textAlign: "right" }}>
              <div className="ag-label" style={{ marginBottom: 5 }}>Your NJM producer</div>
              <div className="ag-body" style={{ fontWeight: 600 }} data-testid="agency-detail-producer">{p.pm_contact.name}</div>
              {p.pm_contact.email && (
                <a className="ag-pm-msg" style={{ justifyContent: "flex-end" }} data-testid="agency-detail-producer-message"
                  href={`mailto:${p.pm_contact.email}?subject=${encodeURIComponent("NJM Productions · Agency Portal")}`}
                  onClick={(e) => handleAgencyMessageClick(e, user?.is_preview)}>
                  Message
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="ag-tabs ag-entrance-2">
        {TABS.map((t) => (
          <button key={t} className={`ag-tab${tab === t ? " active" : ""}`} onClick={() => setTab(t)} data-testid={`agency-tab-${t.replace(/\s/g, "-").toLowerCase()}`}>
            {t}
            {t === "Recce" && recceNeedsInputCount > 0 && (
              <span className="ag-pill ag-pill-neutral" style={{ marginLeft: 6, fontSize: 10 }} data-testid="agency-tab-recce-needs-input-badge">{recceNeedsInputCount}</span>
            )}
          </button>
        ))}
      </div>

      <div className="ag-entrance-3">
        {tab === "Overview" && <Overview ws={ws} crew={crew} projectId={id} />}
        {tab === "BTS" && <AgencyBtsGallery projectId={id} />}
        {tab === "Open items" && <OpenItems items={items} reload={loadItems} projectId={id} />}
        {tab === "Preferred crew" && <Crew crew={crew} reload={loadCrew} projectId={id} nav={nav} />}
        {tab === "Recce" && <AgencyRecce projectId={id} onJumpToTab={setTab} initialRecceId={initialRecceId} />}
        {tab === "Cost sheet" && <AgencyCostSheet projectId={id} projectName={p.name} />}
        {tab === "Documents" && <Documents docs={docs} />}
        {tab === "Activity" && <Activity activity={ws.recent_activity} />}
      </div>
    </div>
  );
}

function Overview({ ws, crew, projectId }) {
  const p = ws.project;
  return (
    <div className="ag-stack">
      <div className="ag-card">
        <h2 className="ag-eyebrow" style={{ marginBottom: 18 }}>Production lifecycle</h2>
        <Lifecycle timeline={p.timeline} />
      </div>
      <div className="ag-card">
        <h2 className="ag-eyebrow" style={{ marginBottom: 14 }}>Crew</h2>
        {(!crew || crew.length === 0) ? <span className="ag-small">No crew requirements yet.</span> : (
          <div className="ag-stack">
            {crew.map((r) => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="ag-body" style={{ fontWeight: 600 }}><Users size={14} style={{ verticalAlign: "-2px", marginRight: 8, color: "#8A8A8A" }} />{r.role} <span className="ag-small">×{r.qty}</span></span>
                {(r.status || "").toLowerCase() === "draft" ? <DraftChip /> : <StatusPill status={r.status} />}
              </div>
            ))}
          </div>
        )}
      </div>
      <AgencyComments projectId={projectId} />
    </div>
  );
}

function AgencyComments({ projectId }) {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => agencyApi.comments(projectId).then((r) => setComments(r.data.items || [])).catch(() => setComments([])), [projectId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true); setMsg("");
    try { await agencyApi.addComment(projectId, text.trim()); setText(""); load(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="ag-card" data-testid="agency-project-comments">
      <h2 className="ag-eyebrow" style={{ marginBottom: 14 }}>Comments</h2>
      {comments === null ? <span className="ag-small">Loading…</span> : comments.length === 0 ? (
        <span className="ag-small">No comments yet — say hello to your NJM team.</span>
      ) : (
        <div className="ag-stack" style={{ marginBottom: 16 }}>
          {comments.map((c) => (
            <div key={c.id} className="ag-small" data-testid="agency-comment-row">
              <strong style={{ color: "var(--ink)" }}>{c.author_type === "partner" ? "You" : "NJM"}:</strong> {c.text} <span style={{ color: "var(--faint)" }}>· {when(c.at)}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
        <textarea className="ag-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment for NJM…" data-testid="agency-comment-input" />
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 10 }}>
          <button className="ag-btn ag-btn-gold" onClick={submit} disabled={busy} data-testid="agency-comment-submit">{busy ? "Sending…" : "Send to NJM"}</button>
          {msg && <span className="ag-small">{msg}</span>}
        </div>
      </div>
    </div>
  );
}

function OpenItems({ items, reload, projectId }) {
  const [showForm, setShowForm] = useState(false);
  return (
    <div className="ag-stack" data-testid="agency-open-items">
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="ag-btn ag-btn-ghost" onClick={() => setShowForm((v) => !v)} data-testid="agency-new-item-toggle">{showForm ? "Cancel" : "+ Add open item"}</button>
      </div>
      {showForm && <NewOpenItemForm projectId={projectId} reload={reload} onDone={() => setShowForm(false)} />}
      {!items.length ? <div className="ag-empty">No open items are shared with you right now.</div> : items.map((it) => <OpenItem key={it.id} it={it} reload={reload} />)}
    </div>
  );
}

function NewOpenItemForm({ projectId, reload, onDone }) {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true); setMsg("");
    try {
      await agencyApi.createOpenItem(projectId, { title: title.trim(), details: details.trim(), due: due || null });
      setTitle(""); setDetails(""); setDue(""); reload(); onDone();
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ag-card" data-testid="agency-new-item-form">
      <div className="ag-label" style={{ marginBottom: 8 }}>Title</div>
      <input className="ag-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Need updated brand guidelines" data-testid="agency-new-item-title" />
      <div className="ag-label" style={{ marginTop: 14, marginBottom: 8 }}>Details</div>
      <textarea className="ag-input" rows={2} value={details} onChange={(e) => setDetails(e.target.value)} data-testid="agency-new-item-details" />
      <div className="ag-label" style={{ marginTop: 14, marginBottom: 8 }}>Due date (optional)</div>
      <input className="ag-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="agency-new-item-due" />
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14 }}>
        <button className="ag-btn ag-btn-gold" onClick={submit} disabled={busy || !title.trim()} data-testid="agency-new-item-submit">{busy ? "Creating…" : "Create"}</button>
        {msg && <span className="ag-small">{msg}</span>}
      </div>
    </div>
  );
}

export function OpenItem({ it, reload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const startedByYou = it.created_by_party === "partner";
  // A2 (2026-10): an item YOU started gets a free reply box always — the needs_partner_input
  // gate only ever applied to NJM-started items.
  const canReply = startedByYou || it.needs_partner_input;
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true); setMsg("");
    try { await agencyApi.commentItem(it.id, text.trim()); setText(""); setMsg("Sent to NJM."); reload(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ag-card" data-testid="agency-open-item">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 14 }}>
        <div>
          <div className="ag-h3" style={{ fontSize: 16, marginBottom: 6 }}>{it.title}{startedByYou && <span className="ag-pill ag-pill-neutral" style={{ marginLeft: 10 }} data-testid="agency-item-started-by-you">Started by you</span>}</div>
          {it.details && <div className="ag-small" style={{ marginTop: 4 }}>{it.details}</div>}
          <div className="ag-small">Owner: {it.owner_party}{it.due ? ` · due ${when(it.due)}` : ""} · updated {when(it.last_update)}</div>
        </div>
        <OpenItemStatus status={it.status} />
      </div>
      {it.comments?.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line)" }} className="ag-stack">
          {it.comments.map((c, i) => (
            <div key={i} className="ag-small"><strong style={{ color: "var(--ink)" }}>{c.author_type === "partner" ? "You" : "NJM"}:</strong> {c.text} <span style={{ color: "var(--faint)" }}>· {when(c.at)}</span></div>
          ))}
        </div>
      )}
      {canReply ? (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
          <div className="ag-label" style={{ marginBottom: 8 }}>Your response to NJM</div>
          <textarea className="ag-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add your response…" data-testid="agency-item-comment-input" />
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 10 }}>
            <button className="ag-btn ag-btn-gold" onClick={submit} disabled={busy} data-testid="agency-item-comment-submit">{busy ? "Sending…" : "Send to NJM"}</button>
            {msg && <span className="ag-small">{msg}</span>}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}><span className="ag-pill ag-pill-neutral">No action needed</span></div>
      )}
    </div>
  );
}

function Crew({ crew, reload, projectId, nav }) {
  if (!crew.length) return <div className="ag-empty">No preferred-crew requirements have been created for this production yet.</div>;
  return (
    <div className="ag-stack" data-testid="agency-crew">
      {crew.map((r) => <Requirement key={r.id} r={r} reload={reload} projectId={projectId} nav={nav} />)}
    </div>
  );
}

function Requirement({ r, reload, projectId, nav }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async () => {
    setBusy(true); setMsg("");
    try { await agencyApi.submitReq(r.id); setMsg("Sent to NJM."); reload(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async (slotId) => { try { await agencyApi.removeSlot(slotId); reload(); } catch {} };
  const hasPreferred = r.candidates.some((c) => c.state === "Preferred");
  return (
    <div className="ag-card" data-testid="agency-requirement">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div className="ag-h3" style={{ fontSize: 17 }}>{r.role} <span className="ag-small">×{r.qty}{r.location ? ` · ${r.location}` : ""}</span></div>
          {r.capability && <div className="ag-small" style={{ marginTop: 4 }}>{r.capability}</div>}
        </div>
        {(r.status || "").toLowerCase() === "draft" ? <DraftChip /> : <StatusPill status={r.status} />}
      </div>
      {r.candidates.length === 0 ? (
        <div className="ag-small" style={{ padding: "8px 0" }}>No candidate yet.</div>
      ) : (
        <div className="ag-stack">
          {r.candidates.map((c) => (
            <div key={c.slot_id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <AgencyAvatar freelancerId={c.candidate?.id} hasPhoto={c.candidate?.has_profile_photo} name={c.candidate?.name} className="ag-avatar" style={{ width: 38, height: 38 }} />
              <div style={{ flex: 1 }}>
                <div className="ag-body" style={{ fontWeight: 600, fontSize: 13 }}>{c.candidate?.name || "—"}</div>
                <div className="ag-small">{c.candidate?.primary_role}{c.candidate?.city ? ` · ${c.candidate.city}` : ""}</div>
              </div>
              <StatusPill status={c.state} />
              {c.state === "Preferred" && <button className="ag-link" style={{ display: "inline-flex" }} onClick={() => remove(c.slot_id)} aria-label="Remove"><Trash2 size={15} /></button>}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
        <button className="ag-btn ag-btn-ghost" onClick={() => nav(`/agency/discover?project=${projectId}&req=${r.id}`)} data-testid="agency-browse-discover">Browse Discover</button>
        {r.status !== "Sent" && <button className="ag-btn ag-btn-gold" onClick={submit} disabled={busy || !hasPreferred} data-testid="agency-submit-req">{busy ? "Sending…" : "Send to NJM"}</button>}
        {msg && <span className="ag-small">{msg}</span>}
        <span className="ag-small" style={{ marginLeft: "auto" }}>This goes to NJM</span>
      </div>
    </div>
  );
}

// Regression v6 fix pass (2026-10), item 4 — "some image files still have an issue".
// Two independent root causes, both fixed here:
//   (a) EVERY document went through `window.open(URL.createObjectURL(blob))` AFTER an `await`.
//       A `window.open` that isn't a direct result of the click has lost the user gesture —
//       the popup blocker swallows it silently (no error, no tab), and Safari/iOS refuses a
//       blob: URL in a new tab outright. Non-image docs now open the tab SYNCHRONOUSLY and
//       point it at the blob when ready (same fix PdfPublishPanel.jsx already uses).
//   (b) An IMAGE in a new tab has no app chrome to report a failure, and HEIC/HEIF (every
//       iPhone photo) has no browser decoder at all, so the tab just renders blank. Images now
//       open in THIS app's own Lightbox, which shows a decode failure and offers Download.
const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|tiff?|heic|heif|avif)$/i;
const isImageDoc = (d) => /^image\//i.test(d.mime_type || "") || IMAGE_EXT_RE.test(d.name || "");

function Documents({ docs }) {
  const [openingId, setOpeningId] = useState(null);
  const [openErr, setOpenErr] = useState(null);
  const [viewing, setViewing] = useState(null);

  const downloadDoc = async (d) => {
    try {
      const r = await api.get(d.file_url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = d.name || "document";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { setOpenErr(d.id); }
  };

  const openDoc = async (d) => {
    if (d.external_url) { window.open(d.external_url, "_blank", "noopener,noreferrer"); return; }
    if (!d.file_url) { setOpenErr(d.id); return; }
    setOpenErr(null);
    if (isImageDoc(d)) { setViewing(d); return; }
    const tab = window.open("", "_blank");
    if (!tab) { setOpenErr(d.id); return; }
    setOpeningId(d.id);
    try {
      // Proxied through the NJM backend (same auth session this portal already uses) — never
      // a raw Drive URL, and never a plain <a href> (which can't carry the auth header).
      const r = await api.get(d.file_url, { responseType: "blob" });
      tab.location.href = URL.createObjectURL(r.data);
    } catch {
      tab.close();
      setOpenErr(d.id);
    } finally {
      setOpeningId(null);
    }
  };

  if (!docs.length) return <div className="ag-empty">No documents have been shared with you yet.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }} data-testid="agency-project-docs">
      {groupDocs(docs).map(({ bucket, docs: bdocs }) => (
        <section key={bucket} data-testid={`agency-doc-bucket-${bucket.replace(/[\s&]+/g, "-").toLowerCase()}`}>
          <h3 className="ag-eyebrow" style={{ marginBottom: 12 }}>{bucket}</h3>
          <div className="ag-stack">
            {bdocs.map((d) => (
              <div key={d.id}>
                <button type="button" onClick={() => openDoc(d)} disabled={openingId === d.id} className="ag-card ag-card-hover"
                  style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", textAlign: "left", border: "none", cursor: "pointer" }}
                  data-testid="agency-doc-row">
                  <div>
                    <div className="ag-body" style={{ fontWeight: 600 }}>{d.name} <span className="ag-small">· {d.doc_type} · v{d.version}</span></div>
                    {openingId === d.id && <div className="ag-small">Opening…</div>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}><StatusPill status={d.status} /><ExternalLink size={16} color="#8A8A8A" /></div>
                </button>
                {openErr === d.id && (
                  <div className="ag-small" style={{ color: "#B42318", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }} data-testid={`agency-doc-open-error-${d.id}`}>
                    <FileWarning size={13} /> Couldn't open this document.
                    {d.file_url && <button className="ag-link" onClick={() => downloadDoc(d)} data-testid={`agency-doc-download-fallback-${d.id}`}>Download it instead</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
      {viewing && (
        <Lightbox items={[{ id: viewing.id, caption: viewing.name, filename: viewing.name, url: viewing.file_url }]}
          index={0} onClose={() => setViewing(null)} onNavigate={() => {}} />
      )}
    </div>
  );
}

function Activity({ activity }) {
  if (!activity?.length) return <div className="ag-empty">No recent activity.</div>;
  return (
    <div className="ag-card" data-testid="agency-project-activity">
      {activity.map((a, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "11px 0", borderBottom: i < activity.length - 1 ? "1px solid var(--line)" : "none" }}>
          <span className="ag-body" style={{ fontSize: 13 }}>{a.text}</span>
          <span className="ag-small">{when(a.at)}</span>
        </div>
      ))}
    </div>
  );
}
